import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';

import { Product, Category } from '../types';
import { productService } from '../services/productService';
import { featuredProductService } from '../services/featuredProductService';
import { carouselService, CarouselImage } from '../services/carouselService';
import ProductCard from '../components/ProductCard';
import ResponsiveGrid from '../components/common/ResponsiveGrid';
import { getCategoryImageUrl } from '../utils/categoryUtils';
import { getDefaultCarouselSlides, type CarouselSlide } from '../utils/carouselUtils';

const HomePage: React.FC = () => {
    const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [carouselImages, setCarouselImages] = useState<CarouselImage[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentSlide, setCurrentSlide] = useState(0);

    // Convert carousel images to carousel slides format
    const carouselSlides: CarouselSlide[] = carouselImages.length > 0
        ? carouselImages.map((image, index) => ({
            id: index + 1,
            image: `${image.url}?t=${Date.now()}`, // Add cache busting
            title: image.title || 'Bienvenue chez Eldaï',
            subtitle: image.subtitle || 'Votre grossiste en produits casher de confiance',
            ctaText: image.ctaText || 'Découvrir nos produits',
            ctaLink: image.ctaLink || '/catalog'
        }))
        : getDefaultCarouselSlides();

    useEffect(() => {
        const loadHomeData = async () => {
            try {
                console.log('Starting to load home data...');

                // Load carousel images
                console.log('Loading carousel images...');
                try {
                    const carouselResponse = await carouselService.getCarouselImages();
                    console.log('Carousel images response:', carouselResponse);
                    setCarouselImages(carouselResponse.images);
                } catch (carouselError) {
                    console.error('Error loading carousel images, using defaults:', carouselError);
                    // Keep carouselImages empty to use defaults
                }

                // Load featured products using the new API
                console.log('Loading featured products...');
                const featuredResponse = await featuredProductService.getFeaturedProductsPublic(6);
                console.log('Featured products response:', featuredResponse);
                setFeaturedProducts(featuredResponse.products);

                // Load categories with S3 images
                console.log('Loading categories...');
                const categoriesResponse = await productService.getCategories();
                console.log('Categories response:', categoriesResponse);
                console.log('Categories loaded:', categoriesResponse.categories);
                setCategories(categoriesResponse.categories);

            } catch (error) {
                console.error('Erreur lors du chargement des données:', error);
            } finally {
                console.log('Setting loading to false');
                setLoading(false);
            }
        };

        loadHomeData();
    }, []);

    // Auto-play carousel
    useEffect(() => {
        if (carouselSlides.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [carouselSlides.length]);

    const getCategoryIcon = (categoryName: string) => {
        const name = categoryName.toLowerCase();

        const iconMap: { [key: string]: string } = {
            'viandes': '🥩',
            'poissons': '🐟',
            'légumes': '🥬',
            'fruits': '🍎',
            'épicerie': '🛒',
            'epicerie': '🛒',
            'pain': '🥖',
            'boulangerie': '🥖',
            'patisserie': '🥖',
            'boissons': '🥤',
            'chocolat': '🍫',
            'confiserie': '🍫',
            'laitiers': '🥛',
            'frais': '🥛',
            'surgelés': '🧊'
        };

        for (const [keyword, icon] of Object.entries(iconMap)) {
            if (name.includes(keyword)) {
                return icon;
            }
        }

        return '📦';
    };

    const renderCategoryCard = (category: Category, keyPrefix: string) => {
        return (
            <Link
                key={`${keyPrefix}-${category.id}`}
                to={`/catalog?categories=${category.id}`}
                className="category-carousel-card"
            >
                <div className="category-card-image">
                    {(() => {
                        // First, try to use the S3 image if available
                        if (category.s3_image) {
                            return (
                                <img
                                    src={category.s3_image.url}
                                    alt={category.name}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            );
                        }

                        // Fallback to database image_url if available
                        if (category.image_url) {
                            return (
                                <img
                                    src={category.image_url}
                                    alt={category.name}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            );
                        }

                        // Final fallback to category icon
                        return (
                            <div className="category-placeholder">
                                <div className="category-icon">
                                    {getCategoryIcon(category.name)}
                                </div>
                            </div>
                        );
                    })()}
                    <div className="category-overlay">
                        <h3 className="category-title">
                            {category.name}
                        </h3>
                        {category.description && (
                            <p className="category-description">
                                {category.description}
                            </p>
                        )}
                    </div>
                </div>
            </Link>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-white">
                <div className="text-center">
                    <div className="spinner mb-4"></div>
                    <div className="text-gray-600 text-lg font-medium">Chargement...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Carousel Section */}
            <section className="hero-carousel-section container mx-auto px-4 pt-8 pb-12">
                <div className="hero-carousel">
                    <div className="carousel-container">
                        {carouselSlides.map((slide: CarouselSlide, index: number) => (
                            <div
                                key={slide.id}
                                className={`carousel-slide ${index === currentSlide ? 'active' : ''}`}
                            >
                                <div className="slide-content">
                                    <div className="slide-image">
                                        <img
                                            src={slide.image}
                                            alt={`Slide ${index + 1}`}
                                            loading="lazy"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Navigation Buttons */}
                    <button
                        className="carousel-nav-btn carousel-prev"
                        onClick={() => setCurrentSlide((prev) => (prev - 1 + carouselSlides.length) % carouselSlides.length)}
                        aria-label="Previous slide"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                    <button
                        className="carousel-nav-btn carousel-next"
                        onClick={() => setCurrentSlide((prev) => (prev + 1) % carouselSlides.length)}
                        aria-label="Next slide"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>

                    {/* Pagination Dots */}
                    <div className="carousel-pagination">
                        {carouselSlides.map((_: CarouselSlide, index: number) => (
                            <button
                                key={index}
                                className={`pagination-dot ${index === currentSlide ? 'active' : ''}`}
                                onClick={() => setCurrentSlide(index)}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-eldai-light-blue mb-4">
                            Pourquoi choisir Eldaï ?
                        </h2>
                        <p className="text-gray-400 text-lg max-w-2xl mx-auto w-full">
                            Découvrez les avantages qui font de nous votre partenaire de confiance
                        </p>
                    </div>
                    <div className="features-grid">
                        <div className="feature-card group">
                            <div className="feature-card-inner">
                                <div className="feature-icon-container">
                                    <div className="feature-icon">🚚</div>
                                    <div className="feature-icon-bg"></div>
                                </div>
                                <div className="feature-content">
                                    <h3 className="feature-title">Livraison rapide</h3>
                                    <p className="feature-description">
                                        Livraison dans toute la France, et en 24-48h en Île-de-France. Service client dédié.
                                    </p>
                                </div>
                                <div className="feature-decoration"></div>
                            </div>
                        </div>
                        <div className="feature-card group">
                            <div className="feature-card-inner">
                                <div className="feature-icon-container">
                                    <div className="feature-icon">✅</div>
                                    <div className="feature-icon-bg"></div>
                                </div>
                                <div className="feature-content">
                                    <h3 className="feature-title">Produits casher</h3>
                                    <p className="feature-description">
                                        Tous nos produits sont certifiés casher selon les normes les plus strictes et contrôlés régulièrement
                                    </p>
                                </div>
                                <div className="feature-decoration"></div>
                            </div>
                        </div>
                        <div className="feature-card group">
                            <div className="feature-card-inner">
                                <div className="feature-icon-container">
                                    <div className="feature-icon">💰</div>
                                    <div className="feature-icon-bg"></div>
                                </div>
                                <div className="feature-content">
                                    <h3 className="feature-title">Paiement sécurisé</h3>
                                    <p className="feature-description">
                                        Paiement à la livraison pour votre sécurité et votre tranquillité, sans risque ni engagement
                                    </p>
                                </div>
                                <div className="feature-decoration"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Categories Carousel Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="text-4xl font-bold text-gray-900 text-center mb-16">
                        Nos catégories
                    </h2>

                    {categories.length > 0 ? (
                        <div className="categories-slick-container">
                            <Slider
                                infinite={true}
                                autoplay={true}
                                autoplaySpeed={0}
                                speed={3000}
                                cssEase="linear"
                                slidesToShow={4}
                                slidesToScroll={1}
                                arrows={false}
                                dots={false}
                                pauseOnHover={true}
                                responsive={[
                                    {
                                        breakpoint: 1280,
                                        settings: {
                                            slidesToShow: 3,
                                            slidesToScroll: 1,
                                        }
                                    },
                                    {
                                        breakpoint: 1024,
                                        settings: {
                                            slidesToShow: 2.5,
                                            slidesToScroll: 1,
                                        }
                                    },
                                    {
                                        breakpoint: 768,
                                        settings: {
                                            slidesToShow: 2,
                                            slidesToScroll: 1,
                                        }
                                    },
                                    {
                                        breakpoint: 480,
                                        settings: {
                                            slidesToShow: 1.5,
                                            slidesToScroll: 1,
                                            arrows: false,
                                        }
                                    },
                                    {
                                        breakpoint: 320,
                                        settings: {
                                            slidesToShow: 1.2,
                                            slidesToScroll: 1,
                                            arrows: false,
                                        }
                                    }
                                ]}
                                className="categories-slider"
                            >
                                {categories.map((category) => (
                                    <div key={category.id} className="slider-item">
                                        {renderCategoryCard(category, `slider-${category.id}`)}
                                    </div>
                                ))}
                            </Slider>
                        </div>
                    ) : (
                        <div className="text-center text-gray-500">
                            <p>Chargement des catégories...</p>
                        </div>
                    )}
                </div>
            </section>

            {/* Featured Products Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-eldai-light-blue mb-4">
                            Produits en vedette
                        </h2>
                        <p className="text-gray-400 text-lg max-w-2xl mx-auto w-full">
                            Découvrez notre sélection de produits casher de qualité premium
                        </p>
                    </div>
                    <ResponsiveGrid>
                        {featuredProducts.map((product) => (
                            <ProductCard key={product.id} product={product} />
                        ))}
                    </ResponsiveGrid>
                    <div className="text-center mt-16">
                        <Link
                            to="/catalog"
                            className="btn btn-primary btn-lg shadow-lg hover:shadow-xl"
                        >
                            Voir tous les produits
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
